import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { teamsAPI, vehiclesAPI } from "@/lib/api";
import { HeaderIcon } from "@/lib/assets";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Users,
  Truck,
  Crown,
  UserPlus,
  Copy,
  RefreshCw,
  Trash2,
  Settings,
  Shield,
  Edit,
  Eye,
  Plus,
  X,
} from "lucide-react";

const ROLE_ICONS = {
  admin: Shield,
  editor: Edit,
  viewer: Eye,
};

const ROLE_COLORS = {
  admin: "bg-amber-500/20 text-amber-500 border-amber-500/30",
  editor: "bg-blue-500/20 text-blue-500 border-blue-500/30",
  viewer: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
};

export default function TeamDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState(null);
  const [myVehicles, setMyVehicles] = useState([]);
  
  // Modals
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [addVehicleOpen, setAddVehicleOpen] = useState(false);
  const [editRoleOpen, setEditRoleOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  
  // Form state
  const [memberEmail, setMemberEmail] = useState("");
  const [memberRole, setMemberRole] = useState("viewer");
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [editingMember, setEditingMember] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadTeam();
    loadMyVehicles();
  }, [id]);

  const loadTeam = async () => {
    try {
      const { data } = await teamsAPI.get(id);
      setTeam(data);
    } catch (error) {
      toast.error("Failed to load team");
      navigate("/teams");
    } finally {
      setLoading(false);
    }
  };

  const loadMyVehicles = async () => {
    try {
      const { data } = await vehiclesAPI.list();
      setMyVehicles(data.vehicles || []);
    } catch (error) {
      console.error("Failed to load vehicles:", error);
    }
  };

  const copyInviteCode = () => {
    if (team?.invite_code) {
      navigator.clipboard.writeText(team.invite_code);
      toast.success("Invite code copied!");
    }
  };

  const handleRegenerateCode = async () => {
    try {
      const { data } = await teamsAPI.regenerateInvite(id);
      setTeam(prev => ({ ...prev, invite_code: data.invite_code }));
      toast.success("New invite code generated");
    } catch (error) {
      toast.error("Failed to regenerate code");
    }
  };

  const handleAddMember = async () => {
    if (!memberEmail.trim()) {
      toast.error("Email is required");
      return;
    }

    setSaving(true);
    try {
      await teamsAPI.addMember(id, { email: memberEmail, role: memberRole });
      toast.success("Member added!");
      setAddMemberOpen(false);
      setMemberEmail("");
      setMemberRole("viewer");
      loadTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add member");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!editingMember || !newRole) return;

    setSaving(true);
    try {
      await teamsAPI.updateMember(id, editingMember.user_id, { role: newRole });
      toast.success("Role updated");
      setEditRoleOpen(false);
      setEditingMember(null);
      loadTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!confirm("Remove this member from the team?")) return;

    try {
      await teamsAPI.removeMember(id, userId);
      toast.success("Member removed");
      loadTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove member");
    }
  };

  const handleAddVehicle = async () => {
    if (!selectedVehicle) {
      toast.error("Select a vehicle");
      return;
    }

    setSaving(true);
    try {
      await teamsAPI.addVehicle(id, selectedVehicle);
      toast.success("Vehicle added to team!");
      setAddVehicleOpen(false);
      setSelectedVehicle("");
      loadTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add vehicle");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveVehicle = async (vehicleId) => {
    if (!confirm("Remove this vehicle from the team?")) return;

    try {
      await teamsAPI.removeVehicle(id, vehicleId);
      toast.success("Vehicle removed");
      loadTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove vehicle");
    }
  };

  const handleDeleteTeam = async () => {
    try {
      await teamsAPI.delete(id);
      toast.success("Team deleted");
      navigate("/teams");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete team");
    }
  };

  const openEditRole = (member) => {
    setEditingMember(member);
    setNewRole(member.role);
    setEditRoleOpen(true);
  };

  // Filter vehicles not already in team
  const availableVehicles = myVehicles.filter(
    v => !team?.vehicles?.some(tv => tv.id === v.id)
  );

  const isAdmin = team?.my_role === "admin";
  const isOwner = team?.owner_id === team?.members?.find(m => m.role === "admin" && team.owner_id)?.user_id;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!team) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link
                to="/teams"
                data-testid="back-btn"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <HeaderIcon />

                <div>
                  <h1 className="text-lg font-bold text-foreground">{team.name}</h1>
                  <span className={`text-xs px-2 py-0.5 rounded-sm border ${ROLE_COLORS[team.my_role]}`}>
                    {team.my_role}
                  </span>
                </div>
              </div>
            </div>
            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeleteConfirmOpen(true)}
                className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Invite Code (Admin Only) */}
        {isAdmin && team.invite_code && (
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Invite Code</p>
                  <p className="text-2xl font-mono font-bold text-foreground tracking-wider">
                    {team.invite_code}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyInviteCode}
                    className="border-zinc-700"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRegenerateCode}
                    className="border-zinc-700"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Members */}
        <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-muted-foreground" />
              Members ({team.members?.length || 0})
            </CardTitle>
            {isAdmin && (
              <Button
                size="sm"
                onClick={() => setAddMemberOpen(true)}
                data-testid="add-member-btn"
                className="btn-tactical bg-primary text-primary-foreground"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Member
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {team.members?.map((member) => {
                const RoleIcon = ROLE_ICONS[member.role] || Eye;
                const isTeamOwner = team.owner_id === member.user_id;
                
                return (
                  <div
                    key={member.user_id}
                    className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-sm border border-zinc-800"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${ROLE_COLORS[member.role]}`}>
                        <RoleIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-foreground">{member.name}</p>
                          {isTeamOwner && (
                            <Crown className="w-4 h-4 text-amber-500" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-sm border ${ROLE_COLORS[member.role]}`}>
                        {member.role}
                      </span>
                      {isAdmin && !isTeamOwner && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEditRole(member)}
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-400"
                            onClick={() => handleRemoveMember(member.user_id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Vehicles */}
        <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Truck className="w-5 h-5 text-muted-foreground" />
              Shared Vehicles ({team.vehicles?.length || 0})
            </CardTitle>
            {(isAdmin || team.my_role === "editor") && availableVehicles.length > 0 && (
              <Button
                size="sm"
                onClick={() => setAddVehicleOpen(true)}
                data-testid="add-vehicle-btn"
                className="btn-tactical bg-primary text-primary-foreground"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Vehicle
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {team.vehicles?.length === 0 ? (
              <div className="text-center py-8">
                <Truck className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-muted-foreground">No vehicles shared yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {team.vehicles?.map((vehicle) => (
                  <div
                    key={vehicle.id}
                    className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-sm border border-zinc-800"
                  >
                    <Link
                      to={`/vehicles/${vehicle.id}`}
                      className="flex items-center gap-3 flex-1 hover:opacity-80"
                    >
                      {vehicle.primary_photo ? (
                        <img
                          src={vehicle.primary_photo}
                          alt={vehicle.nickname}
                          className="w-12 h-12 rounded-sm object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-zinc-800 rounded-sm flex items-center justify-center">
                          <Truck className="w-6 h-6 text-zinc-600" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-foreground">{vehicle.nickname}</p>
                        <p className="text-sm text-muted-foreground">
                          {[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ")}
                        </p>
                        {vehicle.owner_name && (
                          <p className="text-xs text-muted-foreground">
                            Owner: {vehicle.owner_name}
                          </p>
                        )}
                      </div>
                    </Link>
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-400"
                        onClick={() => handleRemoveVehicle(vehicle.id)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add Member Modal */}
      <Dialog open={addMemberOpen} onOpenChange={setAddMemberOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="member-email">Email Address</Label>
              <Input
                id="member-email"
                type="email"
                value={memberEmail}
                onChange={(e) => setMemberEmail(e.target.value)}
                placeholder="user@example.com"
                className="bg-zinc-900 border-zinc-800"
                data-testid="member-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="member-role">Role</Label>
              <Select value={memberRole} onValueChange={setMemberRole}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  <SelectItem value="admin">Admin - Full control</SelectItem>
                  <SelectItem value="editor">Editor - Can edit vehicles</SelectItem>
                  <SelectItem value="viewer">Viewer - Read only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMemberOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button
              onClick={handleAddMember}
              disabled={saving}
              className="btn-tactical bg-primary text-primary-foreground"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Modal */}
      <Dialog open={editRoleOpen} onOpenChange={setEditRoleOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Change role for <strong>{editingMember?.name}</strong>
            </p>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="editor">Editor</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRoleOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button
              onClick={handleUpdateRole}
              disabled={saving}
              className="btn-tactical bg-primary text-primary-foreground"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Vehicle Modal */}
      <Dialog open={addVehicleOpen} onOpenChange={setAddVehicleOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle>Add Vehicle to Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800">
                <SelectValue placeholder="Select a vehicle" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                {availableVehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.nickname} - {v.year} {v.make} {v.model}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddVehicleOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button
              onClick={handleAddVehicle}
              disabled={saving || !selectedVehicle}
              className="btn-tactical bg-primary text-primary-foreground"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add to Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent className="bg-zinc-950 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Team?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the team and remove all members. Vehicles will not be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTeam}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete Team
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
